/**
 * VisualConfigurator.js
 * Interactive garment configurator with live SVG preview and step-by-step option cards.
 * Inspired by SuitSupply / Bombay Shirt Company configurator UX.
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Check, ChevronLeft, ChevronRight, RotateCcw, List, Sparkles, Info } from 'lucide-react';
import GarmentIllustration from './GarmentIllustration';
import { configuratorIllustrationsAPI } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveIllustrationUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
};

/* ============================================================
   STEP DEFINITIONS PER GARMENT TYPE
   Maps each garment type to an ordered list of configurator steps.
   Each step contains a subset of the spec parameters with visual
   card options where applicable.
   ============================================================ */
const CONFIGURATOR_STEPS = {
  jacket: [
    { id: 'silhouette', label: 'Silhouette', keys: ['button_config'], visual: true,
      description: 'Choose single or double-breasted and button count' },
    { id: 'lapel', label: 'Lapel', keys: ['lapel_style', 'lapel_width'], visual: true,
      description: 'Select lapel shape and width' },
    { id: 'pockets', label: 'Pockets', keys: ['pocket_style', 'pocket_shape', 'ticket_pocket'], visual: true,
      description: 'Pocket style and extras' },
    { id: 'construction', label: 'Construction', keys: ['canvas', 'shoulder', 'lining', 'button_stance'], visual: false,
      description: 'Internal structure and shoulder type' },
    { id: 'details', label: 'Details', keys: ['vent', 'pick_stitching', 'buttonhole_colour', 'monogram', 'monogram_text', 'monogram_placement', 'monogram_colour'], visual: true,
      description: 'Finishing touches and personalization' },
  ],
  trousers: [
    { id: 'waistband', label: 'Waistband & Closure', keys: ['waistband', 'closure', 'pleat'], visual: true,
      description: 'Waistband style, closure type, and pleat' },
    { id: 'pockets', label: 'Pockets', keys: ['pocket', 'back_pocket'], visual: true,
      description: 'Front and back pocket styles' },
    { id: 'details', label: 'Details', keys: ['cuff', 'cuff_width', 'suspender_buttons', 'monogram', 'monogram_text'], visual: true,
      description: 'Turn-ups and finishing details' },
  ],
  shirt: [
    { id: 'collar', label: 'Collar', keys: ['collar', 'collar_stays'], visual: true,
      description: 'Collar shape defines the shirt character' },
    { id: 'cuff', label: 'Cuff', keys: ['cuff'], visual: true,
      description: 'Barrel, French or double cuff' },
    { id: 'body', label: 'Body', keys: ['placket', 'hem'], visual: true,
      description: 'Placket and hem construction' },
    { id: 'details', label: 'Details', keys: ['pocket', 'monogram', 'monogram_text', 'monogram_placement', 'monogram_font', 'monogram_colour'], visual: true,
      description: 'Pockets and personalization' },
  ],
  sherwani: [
    { id: 'collar', label: 'Collar', keys: ['collar'], visual: true,
      description: 'Neckline style for the sherwani' },
    { id: 'body', label: 'Buttons & Lining', keys: ['button_count', 'lining'], visual: true,
      description: 'Closure and internal lining' },
  ],
  bandhgala: [
    { id: 'body', label: 'Body', keys: ['collar', 'button_count', 'vent', 'lining'], visual: true,
      description: 'Bandhgala silhouette and structure' },
    { id: 'pockets', label: 'Pockets', keys: ['pocket'], visual: true, description: 'Pocket style' },
    { id: 'embroidery', label: 'Embroidery', keys: ['embroidery', 'embroidery_thread'], visual: true,
      description: 'Embroidery details' },
  ],
  nehru_vest: [
    { id: 'body', label: 'Body', keys: ['collar', 'button_count', 'lining'], visual: true,
      description: 'Vest silhouette' },
    { id: 'pockets', label: 'Pockets', keys: ['pocket'], visual: true, description: 'Pocket style' },
  ],
  kurta: [
    { id: 'collar', label: 'Collar', keys: ['collar'], visual: true, description: 'Neckline style' },
    { id: 'body', label: 'Body', keys: ['length', 'cuff'], visual: true,
      description: 'Length and cuff style' },
    { id: 'details', label: 'Details', keys: ['pocket', 'embroidery'], visual: true,
      description: 'Pockets and embroidery' },
  ],
  pyjama: [
    { id: 'style', label: 'Style', keys: ['style', 'waistband', 'cuff'], visual: true,
      description: 'Pyjama shape and construction' },
  ],
  waistcoat: [
    { id: 'lapel', label: 'Lapel', keys: ['lapel'], visual: true, description: 'Lapel or no-lapel' },
    { id: 'body', label: 'Body', keys: ['button_count', 'length', 'back'], visual: true,
      description: 'Button count, length and back detail' },
    { id: 'pockets', label: 'Pockets', keys: ['pocket'], visual: true, description: 'Pocket style' },
  ],
};

/* ============================================================
   Construction info tooltips (non-visual params)
   ============================================================ */
const CONSTRUCTION_INFO = {
  canvas: {
    full: 'Finest construction. The canvas extends through the entire front for the best drape and longevity.',
    half: 'Best value. Canvas in the chest and lapel area, fused below. Our recommended default.',
  },
  shoulder: {
    structured: 'Moderate padding for a classic, defined shoulder. Traditional British style.',
    roped: 'Extended shoulder with a gathered sleeve head. Bold Italian statement.',
    soft_neapolitan: 'Unpadded, relaxed shoulder with visible gathering. Very casual elegance.',
  },
  lining: {
    full: 'Fully lined interior. Easiest to slip on and off. Best for heavier fabrics.',
    half: 'Lined in the upper body, unlined below. Good balance of comfort and finish.',
    quarter: 'Minimal lining. Lightest option. Shows the inner construction.',
    unlined: 'No lining at all. Maximum breathability for summer.',
  },
};

/* ============================================================
   MINI ILLUSTRATIONS for option cards
   Small SVG previews showing what each option looks like.
   ============================================================ */
function MiniLapel({ style }) {
  return (
    <svg viewBox="0 0 60 50" className="w-full h-full">
      {style === 'notch' && <>
        <path d="M30,15 L18,10 L12,22 L22,38 L30,34" fill="none" stroke="#1B2A4A" strokeWidth="1.2" />
        <path d="M30,15 L42,10 L48,22 L38,38 L30,34" fill="none" stroke="#1B2A4A" strokeWidth="1.2" />
        <line x1="18" y1="10" x2="14" y2="4" stroke="#1B2A4A" strokeWidth="1.2" />
        <line x1="42" y1="10" x2="46" y2="4" stroke="#1B2A4A" strokeWidth="1.2" />
      </>}
      {style === 'peak' && <>
        <path d="M30,15 L18,12 L12,22 L22,40 L30,35" fill="none" stroke="#1B2A4A" strokeWidth="1.2" />
        <path d="M30,15 L42,12 L48,22 L38,40 L30,35" fill="none" stroke="#1B2A4A" strokeWidth="1.2" />
        <line x1="18" y1="12" x2="6" y2="3" stroke="#1B2A4A" strokeWidth="1.2" />
        <line x1="42" y1="12" x2="54" y2="3" stroke="#1B2A4A" strokeWidth="1.2" />
      </>}
      {style === 'shawl' && <>
        <path d="M30,15 Q18,12 12,22 Q16,34 30,38" fill="none" stroke="#1B2A4A" strokeWidth="1.2" />
        <path d="M30,15 Q42,12 48,22 Q44,34 30,38" fill="none" stroke="#1B2A4A" strokeWidth="1.2" />
      </>}
    </svg>
  );
}

function MiniPocket({ style }) {
  return (
    <svg viewBox="0 0 60 40" className="w-full h-full">
      {style === 'flap' && <>
        <rect x="10" y="8" width="40" height="5" rx="1" fill="rgba(27,42,74,0.06)" stroke="#1B2A4A" strokeWidth="1" />
        <rect x="10" y="13" width="40" height="20" rx="1" fill="none" stroke="rgba(27,42,74,0.2)" strokeWidth="0.7" strokeDasharray="3,2" />
      </>}
      {style === 'jetted' && <>
        <line x1="10" y1="15" x2="50" y2="15" stroke="#1B2A4A" strokeWidth="1" />
        <line x1="10" y1="20" x2="50" y2="20" stroke="#1B2A4A" strokeWidth="1" />
      </>}
      {style === 'patch' && <rect x="10" y="6" width="40" height="28" rx="4" fill="none" stroke="#1B2A4A" strokeWidth="1" />}
      {style === 'welt' && <line x1="10" y1="18" x2="50" y2="18" stroke="#1B2A4A" strokeWidth="1.2" />}
    </svg>
  );
}

function MiniButton({ config }) {
  const isDB = config?.startsWith('DB');
  return (
    <svg viewBox="0 0 50 60" className="w-full h-full">
      <rect x="12" y="2" width="26" height="56" rx="3" fill="rgba(27,42,74,0.03)" stroke="#1B2A4A" strokeWidth="0.8" />
      <line x1="25" y1="5" x2="25" y2="55" stroke="rgba(27,42,74,0.15)" strokeWidth="0.5" />
      {!isDB && config === '1B' && <circle cx="25" cy="30" r="3" fill="none" stroke="#1B2A4A" strokeWidth="0.8" />}
      {!isDB && config === '2B' && <>
        <circle cx="25" cy="22" r="2.5" fill="none" stroke="#1B2A4A" strokeWidth="0.8" />
        <circle cx="25" cy="35" r="2.5" fill="none" stroke="#1B2A4A" strokeWidth="0.8" />
      </>}
      {!isDB && config === '3B' && <>
        <circle cx="25" cy="16" r="2.5" fill="none" stroke="#1B2A4A" strokeWidth="0.8" />
        <circle cx="25" cy="28" r="2.5" fill="none" stroke="#1B2A4A" strokeWidth="0.8" />
        <circle cx="25" cy="40" r="2.5" fill="none" stroke="#1B2A4A" strokeWidth="0.8" />
      </>}
      {isDB && <>
        <circle cx="20" cy="18" r="2" fill="none" stroke="#1B2A4A" strokeWidth="0.7" />
        <circle cx="20" cy="30" r="2" fill="none" stroke="#1B2A4A" strokeWidth="0.7" />
        <circle cx="30" cy="18" r="2" fill="#1B2A4A" stroke="#1B2A4A" strokeWidth="0.7" />
        <circle cx="30" cy="30" r="2" fill="#1B2A4A" stroke="#1B2A4A" strokeWidth="0.7" />
        <circle cx="20" cy="42" r="2" fill="none" stroke="#1B2A4A" strokeWidth="0.7" />
        {config === 'DB_6x2' && <circle cx="30" cy="42" r="2" fill="#1B2A4A" stroke="#1B2A4A" strokeWidth="0.7" />}
      </>}
    </svg>
  );
}

/* ============================================================
   OPTION CARD
   Visual selection card with illustration, label, selected state
   ============================================================ */
function OptionCard({ value, label, isSelected, isDefault, onClick, children, testId, customImageUrl }) {
  return (
    <button onClick={onClick}
      className="relative flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-150 hover:shadow-sm min-w-[80px]"
      style={{
        borderColor: isSelected ? '#C5A55A' : 'rgba(27,42,74,0.08)',
        backgroundColor: isSelected ? 'rgba(197,165,90,0.04)' : 'white',
        boxShadow: isSelected ? '0 0 0 1px rgba(197,165,90,0.2)' : 'none',
      }}
      data-testid={testId || `option-card-${value}`}
    >
      {/* Selected checkmark */}
      {isSelected && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#C5A55A' }}>
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        </div>
      )}
      {/* Recommended badge */}
      {isDefault && !isSelected && (
        <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 text-[7px] px-1.5 py-0 rounded-full whitespace-nowrap"
          style={{ backgroundColor: 'rgba(27,42,74,0.06)', color: '#6B7280' }}>
          Recommended
        </Badge>
      )}
      {/* Custom illustration or SVG fallback */}
      <div className="w-16 h-12 flex items-center justify-center overflow-hidden">
        {customImageUrl ? (
          <img src={resolveIllustrationUrl(customImageUrl)} alt={label}
            className="max-w-full max-h-full object-contain" />
        ) : children}
      </div>
      {/* Label */}
      <span className="text-[11px] font-medium text-center leading-tight"
        style={{ color: isSelected ? '#1B2A4A' : '#6B7280' }}>{label}</span>
    </button>
  );
}

/* ============================================================
   VISUAL CONFIGURATOR - MAIN EXPORT
   ============================================================ */
export function VisualConfigurator({ garmentType, spec, onChange, params, defaults, fabricColor, fabricSwatch }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [simpleView, setSimpleView] = useState(false);
  const [illustrationLookup, setIllustrationLookup] = useState({});

  const steps = useMemo(() => CONFIGURATOR_STEPS[garmentType] || [], [garmentType]);
  const step = steps[currentStep] || steps[0];

  // Reset step when garment type changes
  useEffect(() => { setCurrentStep(0); }, [garmentType]);

  // Fetch custom illustrations for this garment type
  useEffect(() => {
    if (!garmentType) return;
    configuratorIllustrationsAPI.lookup(garmentType)
      .then(res => setIllustrationLookup(res.data?.lookup || {}))
      .catch(() => {});
  }, [garmentType]);

  const handleChange = useCallback((key, value) => {
    onChange({ ...spec, [key]: value });
  }, [spec, onChange]);

  const handleReset = () => { onChange(defaults || {}); };

  const paramMap = useMemo(() => {
    const m = {};
    for (const p of (params || [])) { m[p.key] = p; }
    return m;
  }, [params]);

  const customCount = Object.keys(spec || {}).filter(k => {
    if (!defaults || defaults[k] === undefined) return !!spec[k];
    return spec[k] !== defaults[k];
  }).length;

  // Build fabric color from swatch or explicit color
  const fillColor = fabricColor || (fabricSwatch ? undefined : 'rgba(27,42,74,0.03)');

  if (!params || params.length === 0) return null;

  /* ============ SIMPLE VIEW (dropdown fallback) ============ */
  if (simpleView) {
    return (
      <div className="mt-3 rounded-xl border p-4 space-y-4" style={{ borderColor: 'rgba(197,165,90,0.25)', backgroundColor: 'rgba(27,42,74,0.01)' }}
        data-testid={`configurator-simple-${garmentType}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--brand-navy)' }}>
              Design Customisation
            </span>
            {customCount > 0 && (
              <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: 'rgba(197,165,90,0.15)', color: '#92700C' }}>
                {customCount} custom
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1" onClick={handleReset}>
              <RotateCcw className="w-3 h-3" /> Reset
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
              onClick={() => setSimpleView(false)} data-testid="switch-to-visual">
              <Sparkles className="w-3 h-3" /> Visual View
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2.5">
          {params.map(p => (
            <div key={p.key} data-testid={`spec-field-${p.key}`}>
              <Label className="text-[10px] font-medium uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{p.label}</Label>
              {p.type === 'text' ? (
                <Input value={spec[p.key] || ''} onChange={e => handleChange(p.key, e.target.value)}
                  placeholder={p.placeholder || ''} className="h-8 text-xs rounded-md mt-1" />
              ) : (
                <Select value={spec[p.key] || ''} onValueChange={v => handleChange(p.key, v)}>
                  <SelectTrigger className="h-8 text-xs rounded-md mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(p.options || []).map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {defaults?.[p.key] && spec[p.key] !== defaults[p.key] && (
                <p className="text-[8px] mt-0.5" style={{ color: '#C5A55A' }}>
                  House: {(p.options || []).find(o => o.value === defaults[p.key])?.label || defaults[p.key]}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ============ VISUAL CONFIGURATOR (main view) ============ */
  return (
    <div className="mt-3 rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(197,165,90,0.25)', backgroundColor: 'white' }}
      data-testid={`visual-configurator-${garmentType}`}>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--brand-navy)' }}>
            Design Configurator
          </span>
          {customCount > 0 && (
            <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: 'rgba(197,165,90,0.15)', color: '#92700C' }}>
              {customCount} custom
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1" onClick={handleReset}>
            <RotateCcw className="w-3 h-3" /> Reset
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
            onClick={() => setSimpleView(true)} data-testid="switch-to-simple">
            <List className="w-3 h-3" /> Simple View
          </Button>
        </div>
      </div>

      {/* Main content: Illustration + Options */}
      <div className="flex flex-col lg:flex-row">
        {/* LEFT: Illustration (60%) */}
        <div className="lg:w-[55%] flex items-center justify-center p-6 lg:p-8 lg:border-r"
          style={{ borderColor: 'rgba(27,42,74,0.06)', backgroundColor: 'rgba(27,42,74,0.015)', minHeight: '400px' }}>
          <div className="w-full max-w-[300px]" style={{ transition: 'all 200ms ease' }}>
            <GarmentIllustration garmentType={garmentType} spec={spec} fabricColor={fillColor} className="w-full h-auto" />
          </div>
        </div>

        {/* RIGHT: Options (40%) */}
        <div className="lg:w-[45%] flex flex-col">
          {/* Step info */}
          <div className="px-4 pt-4 pb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#C5A55A' }}>
              Step {currentStep + 1} of {steps.length}
            </p>
            <h3 className="text-base font-semibold mt-0.5" style={{ color: 'var(--brand-navy)' }}>{step?.label}</h3>
            {step?.description && (
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>{step.description}</p>
            )}
          </div>

          {/* Options for current step */}
          <div className="flex-1 px-4 pb-4 overflow-y-auto" style={{ maxHeight: '400px' }}>
            {step?.keys.map(key => {
              const param = paramMap[key];
              if (!param) return null;
              // Check depends_on
              if (param.depends_on) {
                for (const [dk, dv] of Object.entries(param.depends_on)) {
                  if (spec[dk] !== dv) return null;
                }
              }
              if (param.depends_on_not) {
                for (const [dk, dv] of Object.entries(param.depends_on_not)) {
                  if (spec[dk] === dv) return null;
                }
              }

              const options = param.options || [];
              const currentVal = spec[key] || '';
              const defaultVal = defaults?.[key];

              // Auto-hide parameters with only 1 option (auto-select it)
              if (options.length === 1 && param.type !== 'text') {
                if (currentVal !== options[0].value) {
                  handleChange(key, options[0].value);
                }
                return null;
              }

              const isVisual = step.visual && options.length > 0 && options.length <= 8
                && param.type !== 'text' && param.type !== 'dropdown' && param.type !== 'toggle';

              // Text input fields
              if (param.type === 'text') {
                return (
                  <div key={key} className="mb-3" data-testid={`config-field-${key}`}>
                    <Label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>
                      {param.label}
                    </Label>
                    <Input value={currentVal} onChange={e => handleChange(key, e.target.value)}
                      placeholder={param.placeholder || ''} className="h-9 text-sm rounded-lg mt-1" />
                  </div>
                );
              }

              // Explicit toggle type (e.g. Turn-Up / Cuff)
              if (param.type === 'toggle') {
                return (
                  <div key={key} className="flex items-center justify-between py-2.5 mb-1" data-testid={`config-field-${key}`}>
                    <div>
                      <p className="text-xs font-medium" style={{ color: 'var(--brand-navy)' }}>{param.label}</p>
                    </div>
                    <Switch checked={currentVal === 'yes'} onCheckedChange={v => handleChange(key, v ? 'yes' : 'no')} />
                  </div>
                );
              }

              // Explicit dropdown type (e.g. pleat — no visual cards)
              if (param.type === 'dropdown') {
                return (
                  <div key={key} className="mb-3" data-testid={`config-field-${key}`}>
                    <Label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>
                      {param.label}
                    </Label>
                    <Select value={currentVal} onValueChange={v => handleChange(key, v)}>
                      <SelectTrigger className="h-9 text-xs rounded-lg mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }

              // Toggle fields (yes/no)
              if (options.length === 2 && options.every(o => ['yes', 'no'].includes(o.value))) {
                return (
                  <div key={key} className="flex items-center justify-between py-2.5 mb-1" data-testid={`config-field-${key}`}>
                    <div>
                      <p className="text-xs font-medium" style={{ color: 'var(--brand-navy)' }}>{param.label}</p>
                      {defaultVal && currentVal !== defaultVal && (
                        <p className="text-[8px]" style={{ color: '#C5A55A' }}>House: {defaultVal === 'yes' ? 'Yes' : 'No'}</p>
                      )}
                    </div>
                    <Switch checked={currentVal === 'yes'} onCheckedChange={v => handleChange(key, v ? 'yes' : 'no')} />
                  </div>
                );
              }

              // Visual option cards
              if (isVisual) {
                return (
                  <div key={key} className="mb-4" data-testid={`config-field-${key}`}>
                    <Label className="text-[10px] font-semibold uppercase tracking-wide block mb-2" style={{ color: 'var(--ink-muted)' }}>
                      {param.label}
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {options.map(opt => (
                        <OptionCard key={opt.value} value={opt.value} label={opt.label}
                          isSelected={currentVal === opt.value} isDefault={opt.value === defaultVal}
                          onClick={() => handleChange(key, opt.value)}
                          testId={`option-${key}-${opt.value}`}
                          customImageUrl={illustrationLookup[`${key}|${opt.value}`]}>
                          <MiniIllustration paramKey={key} value={opt.value} />
                        </OptionCard>
                      ))}
                    </div>
                    {/* Construction info */}
                    {CONSTRUCTION_INFO[key]?.[currentVal] && (
                      <div className="mt-2 p-2.5 rounded-lg flex gap-2" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#C5A55A' }} />
                        <p className="text-[10px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                          {CONSTRUCTION_INFO[key][currentVal]}
                        </p>
                      </div>
                    )}
                  </div>
                );
              }

              // Non-visual: dropdown
              return (
                <div key={key} className="mb-3" data-testid={`config-field-${key}`}>
                  <Label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>
                    {param.label}
                  </Label>
                  <Select value={currentVal} onValueChange={v => handleChange(key, v)}>
                    <SelectTrigger className="h-9 text-xs rounded-lg mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {CONSTRUCTION_INFO[key]?.[currentVal] && (
                    <div className="mt-1.5 p-2 rounded-lg flex gap-2" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                      <Info className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: '#C5A55A' }} />
                      <p className="text-[10px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                        {CONSTRUCTION_INFO[key][currentVal]}
                      </p>
                    </div>
                  )}
                  {defaultVal && currentVal !== defaultVal && (
                    <p className="text-[8px] mt-0.5" style={{ color: '#C5A55A' }}>
                      House: {options.find(o => o.value === defaultVal)?.label || defaultVal}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Step navigation */}
          <div className="px-4 py-3 border-t flex items-center justify-between" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
            <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg gap-1"
              disabled={currentStep === 0}
              onClick={() => setCurrentStep(s => Math.max(0, s - 1))}
              data-testid="configurator-prev">
              <ChevronLeft className="w-3 h-3" /> Back
            </Button>

            {/* Step dots */}
            <div className="flex items-center gap-1.5" data-testid="configurator-step-dots">
              {steps.map((s, i) => {
                // Check if any key in this step has been customized
                const hasCustom = s.keys.some(k => spec[k] && defaults?.[k] && spec[k] !== defaults[k]);
                return (
                  <button key={i} onClick={() => setCurrentStep(i)}
                    className="w-2.5 h-2.5 rounded-full transition-all"
                    style={{
                      backgroundColor: i === currentStep ? '#1B2A4A'
                        : hasCustom ? '#C5A55A'
                        : 'rgba(27,42,74,0.12)',
                      transform: i === currentStep ? 'scale(1.3)' : 'scale(1)',
                    }}
                    data-testid={`step-dot-${i}`} />
                );
              })}
            </div>

            <Button size="sm" className="h-8 text-xs rounded-lg gap-1"
              style={{ backgroundColor: currentStep === steps.length - 1 ? '#059669' : 'var(--brand-navy)' }}
              disabled={currentStep === steps.length - 1}
              onClick={() => setCurrentStep(s => Math.min(steps.length - 1, s + 1))}
              data-testid="configurator-next">
              Next <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Mini Illustration Router
   Routes param keys to appropriate mini SVGs
   ============================================================ */
function MiniIllustration({ paramKey, value }) {
  // Lapel illustrations
  if (paramKey === 'lapel_style' || paramKey === 'lapel') return <MiniLapel style={value} />;
  // Pocket illustrations
  if (paramKey === 'pocket_style' || paramKey === 'pocket') return <MiniPocket style={value} />;
  // Button configuration
  if (paramKey === 'button_config') return <MiniButton config={value} />;

  // Generic text-based mini illustration for all other params
  return (
    <div className="w-full h-full flex items-center justify-center">
      <span className="text-[10px] font-bold uppercase tracking-wider text-center leading-tight"
        style={{ color: 'rgba(27,42,74,0.35)' }}>
        {value?.replace(/_/g, ' ')}
      </span>
    </div>
  );
}
